import { useEffect, useRef, useState } from "react";
import styles from "./ElfsightWidget.module.css";

const PLATFORM_SRC = "https://elfsightcdn.com/platform.js";

let scriptRequested = false;

/** Inject the (heavy) Elfsight platform script once, on demand. */
function ensurePlatformScript() {
  if (scriptRequested) return;
  if (document.querySelector(`script[src="${PLATFORM_SRC}"]`)) {
    scriptRequested = true;
    return;
  }
  const script = document.createElement("script");
  script.src = PLATFORM_SRC;
  script.async = true;
  document.body.appendChild(script);
  scriptRequested = true;
}

interface ElfsightWidgetProps {
  /** The widget id (the part after `elfsight-app-` in the embed div class). */
  appId: string;
  /** Reserved height while loading, to prevent layout shift. */
  minHeight?: number;
  /** How far ahead of the viewport to start loading. */
  rootMargin?: string;
  className?: string;
}

/**
 * Lazily mounts an Elfsight widget: the platform script is only fetched, and
 * the widget only instantiated, once the placeholder scrolls near the viewport.
 */
export function ElfsightWidget({
  appId,
  minHeight = 440,
  rootMargin = "400px 0px",
  className,
}: ElfsightWidgetProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (active) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      // No IO support — just load it.
      setActive(true);
      ensurePlatformScript();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          ensurePlatformScript();
          setActive(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [active, rootMargin]);

  return (
    <div ref={ref} className={className} style={{ minHeight }}>
      {active ? (
        <div className={`elfsight-app-${appId}`} data-elfsight-app-lazy />
      ) : (
        <div className={styles.placeholder} aria-hidden="true">
          <span className={styles.spinner} />
        </div>
      )}
    </div>
  );
}
